import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form, Field } from 'react-final-form';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import TextFieldAdapter from 'components/shared/field-adapters/TextFieldAdapter';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import requestStatus from 'common/constants/status';
import pqrsSlice from 'store/modules/pqrs';

/**
 * Modal del "botón de ayuda": el cliente abre una PQRS (asunto + descripción). Al enviarse, el
 * backend notifica a los admins (in-app + correo). Monocromo OTRIX, label arriba / error abajo.
 */
function PqrsHelpModal({ open, onClose }) {
  const dispatch = useDispatch();
  const { t } = useTranslation(['pqrs', 'buttons', 'validations']);
  const createStatus = useSelector((state) => state.pqrs.status.create);
  const loading = createStatus === requestStatus.loading;
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  const clearFile = () => { setFile(null); if (fileRef.current) fileRef.current.value = ''; };

  // Cerrar al completar el envío.
  useEffect(() => {
    if (open && createStatus === requestStatus.success) {
      onClose();
      clearFile();
      dispatch(pqrsSlice.actions.resetStatus());
    }
  }, [open, createStatus, onClose, dispatch]);

  const validate = (values) => {
    const errors = {};
    const required = t('required', { ns: 'validations', defaultValue: 'Campo requerido' });
    if (!values.subject || values.subject.trim().length < 3) errors.subject = required;
    if (!values.body || values.body.trim().length < 3) errors.body = required;
    return errors;
  };

  const onSubmit = (values) => {
    dispatch(pqrsSlice.actions.create({ subject: values.subject.trim(), body: values.body.trim(), attachment: file }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, backgroundColor: 'surface.content', backgroundImage: 'none', border: '1px solid', borderColor: 'hairline.main' } }}
    >
      <DialogTitle sx={{ pt: 3, pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <HelpOutlineIcon sx={{ color: 'gold.main', fontSize: 26 }} />
          <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary' }}>
            {t('newTicket', { ns: 'pqrs', defaultValue: 'Nueva solicitud de ayuda' })}
          </Typography>
        </Stack>
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Form
        onSubmit={onSubmit}
        validate={validate}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent sx={{ pt: 1 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                {t('helpIntro', { ns: 'pqrs', defaultValue: 'Cuéntanos qué ocurre y un administrador te responderá desde la app. Evitamos WhatsApp para dar seguimiento a tu caso.' })}
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 0.5 }}>
                    {t('subject', { ns: 'pqrs', defaultValue: 'Asunto' })}
                  </Typography>
                  <Field name="subject" render={({ input, meta }) => (
                    <TextFieldAdapter input={input} meta={meta} fullWidth placeholder={t('subjectPlaceholder', { ns: 'pqrs', defaultValue: 'Ej. Problema con mi pago' })} inputProps={{ maxLength: 160 }} />
                  )} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 0.5 }}>
                    {t('description', { ns: 'pqrs', defaultValue: 'Descripción' })}
                  </Typography>
                  <Field name="body" render={({ input, meta }) => (
                    <TextFieldAdapter input={input} meta={meta} fullWidth multiline rows={4} placeholder={t('descriptionPlaceholder', { ns: 'pqrs', defaultValue: 'Describe brevemente lo que ocurre' })} inputProps={{ maxLength: 2000 }} />
                  )} />
                </Box>
                <Box>
                  {file ? (
                    <Chip size="small" icon={<InsertDriveFileIcon />} label={file.name} onDelete={clearFile} sx={{ maxWidth: '100%' }} />
                  ) : (
                    <Tooltip title={t('attach', { ns: 'pqrs', defaultValue: 'Adjuntar' })}>
                      <Button onClick={() => fileRef.current?.click()} startIcon={<AttachFileIcon />} color="inherit" sx={{ color: 'gold.main', textTransform: 'none', fontWeight: 700 }}>
                        {t('attach', { ns: 'pqrs', defaultValue: 'Adjuntar' })}
                      </Button>
                    </Tooltip>
                  )}
                  <input ref={fileRef} type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) setFile(f); }} />
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
              <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                <Button onClick={onClose} fullWidth variant="outlined" color="inherit" sx={{ py: 1.1, borderRadius: 2, color: 'text.primary', borderColor: 'hairline.strong', fontWeight: 700, textTransform: 'none' }}>
                  {t('cancel', { ns: 'buttons', defaultValue: 'Cancelar' })}
                </Button>
                <Button type="submit" fullWidth disabled={loading} sx={{ ...premiumButtonSx, py: 1.1 }}>
                  {t('send', { ns: 'pqrs', defaultValue: 'Enviar' })}
                </Button>
              </Stack>
            </DialogActions>
          </form>
        )}
      />
    </Dialog>
  );
}

PqrsHelpModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default PqrsHelpModal;
